import { Controller } from "@hotwired/stimulus"

// Tier table comes from the server (which read it from Stripe) so the live
// total can never disagree with what Stripe will charge.
export default class extends Controller {
  static targets = [
    "selected", "empty", "count", "total", "rate", "nudge",
    "pasteBox", "pasteReport", "hiddenFields", "submit", "checkoutError", "tierRow",
    "browseBlock", "browseFilter", "browseList", "browseSummary",
    "contactField", "email", "emailError", "termsDrawer", "terms",
  ]
  static values = {
    tiers: Array, maxPlaces: Number, resolveUrl: String, preselect: Object,
    invitation: String, fixedPlace: Boolean, legacyCart: Boolean,
    locationPlacesUrl: String, destination: Object, analytics: Object,
  }

  // Rendering every row of a large location is the only part that scales badly:
  // the United States holds ~11,800 places. Filtering that many strings costs
  // about a millisecond, so we hold them all and render a slice.
  static VISIBLE_LIMIT = 200

  connect() {
    this.selection = new Map()
    this.location = this.destinationValue
    this.browsePlaces = []

    // Analytics state. render() runs on every keystroke in the email field, so
    // the rate and nudge events compare against what was last reported rather
    // than firing on every pass.
    this.reportedUnitAmount = null
    this.nudgedToward = null
    this.checkoutStarted = false
    this.abandonHandler = () => this.trackAbandon()
    window.addEventListener("pagehide", this.abandonHandler)

    // Coming back from Stripe through the back/forward cache restores this page
    // without re-running connect(), which would otherwise leave checkout
    // disabled from the submit that took them there.
    this.restoreHandler = (event) => { if (event.persisted) this.releaseCheckout() }
    window.addEventListener("pageshow", this.restoreHandler)

    // Assigned proprietors always use the server's current Place. Destination
    // buyers keep their editable selection when returning from Stripe.
    if (!this.fixedPlaceValue) this.restore()
    this.seedPreselection()

    // A cart carried back from Stripe is already in whatever tier it earned, so
    // seed the rate rather than reporting it as a tier the buyer just crossed.
    if (this.selection.size > 0) this.reportedUnitAmount = this.unitAmount(this.selection.size)

    this.render()
    if (!this.fixedPlaceValue && this.selection.size > 0) this.revalidate()
    // A proprietor has no destination and no browse list: their Place is
    // already in the cart, so the whole find-a-place apparatus is absent from
    // the page and its targets do not exist.
    if (this.hasBrowseListTarget) this.loadBrowse()
  }

  openTerms() {
    this.termsDrawerTarget.placement = document.documentElement.clientWidth < 768 ? "bottom" : "end"
    this.termsDrawerTarget.show()
  }

  acceptTerms() {
    this.render()
    if (this.termsTarget.checked) this.termsDrawerTarget.hide()
  }

  disconnect() {
    clearTimeout(this.locationDebounceTimer)
    window.removeEventListener("pagehide", this.abandonHandler)
    window.removeEventListener("pageshow", this.restoreHandler)
  }

  // Several of these elements carry a display utility class (flex, block),
  // which outranks the [hidden] attribute in the cascade -- that is why the
  // Clear button appeared to do nothing: the chip never hid. An inline style
  // always wins, whatever classes the element has.
  toggle(element, visible) {
    element.style.display = visible ? "" : "none"
  }

  get storageKey() {
    return `amplify-picker-selection:${this.invitationValue}`
  }

  get seededKey() {
    return `amplify-picker-seeded:${this.invitationValue}`
  }

  seedPreselection() {
    const preselect = this.preselectValue
    if (!preselect || !preselect.place_id) return

    // Optional search hints seed the editable cart once. Assigned proprietors
    // bypass that history and always use the Place supplied by the server.
    if (!this.fixedPlaceValue) {
      let seeded = []
      try {
        seeded = JSON.parse(sessionStorage.getItem(this.seededKey) || "[]")
      } catch {
        seeded = []
      }
      if (seeded.includes(preselect.place_id)) return

      try {
        sessionStorage.setItem(this.seededKey, JSON.stringify([...seeded, preselect.place_id]))
      } catch {
        // Private-mode storage failures are not worth breaking the page over.
      }

      if (this.selection.size > 0) return
    }

    this.selection.set(preselect.place_id, {
      title: preselect.title, location: preselect.location, slug: preselect.slug
    })
    this.save()
    this.track("place_selected", {
      place_id: preselect.place_id, place_title: preselect.title, source: "preselected"
    })
  }

  restore() {
    try {
      // Only the invitation active before the upgrade may claim the old cart.
      if (this.legacyCartValue && sessionStorage.getItem(this.storageKey) === null) {
        const legacy = sessionStorage.getItem("amplify-picker-selection")
        if (legacy !== null) {
          sessionStorage.setItem(this.storageKey, legacy)
          sessionStorage.removeItem("amplify-picker-selection")
        }
      }
      const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || "[]")
      stored.forEach(([id, place]) => this.selection.set(Number(id), place))
    } catch {
      this.clearStored()
    }
  }

  save() {
    if (this.fixedPlaceValue) return

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selection.entries()]))
    } catch {
      // Private-mode storage failures are not worth breaking the page over.
    }
  }

  clearStored() {
    try {
      sessionStorage.removeItem(this.storageKey)
    } catch {
      // As above.
    }
  }

  // A restored selection is a claim about the past. Ask the server what is
  // still available and drop anything that is not, rather than letting the
  // buyer reach Stripe with a place someone else has since bought.
  async revalidate() {
    const slugs = [...this.selection.values()].map((p) => p.slug).filter(Boolean)
    if (slugs.length !== this.selection.size) return

    const response = await fetch(this.resolveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify({ text: slugs.join("\n") })
    })
    if (!response.ok) return

    const { resolved, rejected } = await response.json()
    this.selection = new Map(resolved.map((p) => [p.place_id, { title: p.title, location: p.location, slug: p.slug }]))
    if (rejected.length) {
      this.pasteReportTarget.innerHTML =
        `<p class="font-bold">${rejected.length} of your places ${rejected.length === 1 ? "is" : "are"} no longer available:</p>
         <ul class="mt-1 list-disc pl-5">${rejected.map((r) => `<li>${esc(r.input)} — ${this.reason(r.reason)}</li>`).join("")}</ul>`
    }
    this.render()
  }

  // === Browse a location ===

  async loadBrowse() {
    if (!this.hasBrowseListTarget) return

    this.browseSummaryTarget.textContent = `Loading places in ${this.location.label}…`
    this.browseListTarget.innerHTML = ""

    const params = new URLSearchParams()
    for (const key of ["city", "state", "country"]) {
      if (this.location[key]) params.set(key, this.location[key])
    }
    if (this.location.coverage_miles) params.set("coverage_miles", this.location.coverage_miles)

    const response = await fetch(`${this.locationPlacesUrlValue}?${params}`, {
      headers: { Accept: "application/json" }
    })
    if (!response.ok) {
      this.browseSummaryTarget.textContent = "Couldn’t load that location. Try again in a moment."
      return
    }

    const { places } = await response.json()
    this.browsePlaces = places
    this.renderBrowse()
  }

  filterBrowse() {
    this.renderBrowse()
  }

  // Every typed word must appear somewhere in the title or location, so
  // "weird austin" finds "Museum of the Weird" in Austin.
  browseMatches() {
    const words = this.browseFilterTarget.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (words.length === 0) return this.browsePlaces

    return this.browsePlaces.filter((p) => {
      const haystack = `${p.title} ${p.location}`.toLowerCase()
      return words.every((word) => haystack.includes(word))
    })
  }

  renderBrowse() {
    if (!this.hasBrowseListTarget) return

    const matches = this.browseMatches()
    const visible = matches.slice(0, this.constructor.VISIBLE_LIMIT)

    this.browseListTarget.innerHTML = visible.map((p) => {
      const added = this.selection.has(p.place_id)
      return `
        <li class="flex items-baseline justify-between gap-4 border-b border-gray-200 py-2">
          <span class="aon-body-small text-aon-black">
            <span class="${added ? "font-bold" : ""}">${esc(p.title)}</span>
            <span class="text-gray-400">·</span>
            <span class="aon-body-smaller text-gray-600">${esc(p.location)}</span>
          </span>
          ${added
            ? `<button type="button" data-action="amplify-picker#remove" data-place-id="${p.place_id}"
                  class="aon-body-smaller shrink-0 text-gray-600 underline hover:text-aon-black">Remove</button>`
            : `<button type="button" data-action="amplify-picker#add"
                  data-place-id="${p.place_id}" data-title="${esc(p.title)}"
                  data-location="${esc(p.location)}" data-slug="${esc(p.slug)}"
                  class="aon-body-smaller shrink-0 font-bold text-aon-brown underline">Add</button>`}
        </li>`
    }).join("")

    if (this.browsePlaces.length === 0) {
      this.browseSummaryTarget.textContent = `No places available in ${this.location.label}.`
    } else if (matches.length > visible.length) {
      this.browseSummaryTarget.textContent =
        `Showing ${visible.length} of ${matches.length} places — keep typing to narrow.`
    } else {
      this.browseSummaryTarget.textContent =
        `${matches.length} ${matches.length === 1 ? "place" : "places"} in ${this.location.label}.`
    }

  }

  // How many places this buyer may still take, or null on the standard ladder.
  // On an offer this is what is left of the invitation rather than what it
  // covers, so it shrinks as they buy. The server enforces it either way.
  get maxPlaces() {
    return this.maxPlacesValue > 0 ? this.maxPlacesValue : null
  }

  atCapacity() {
    return this.maxPlaces !== null && this.selection.size >= this.maxPlaces
  }

  reportCapacity() {
    this.checkoutErrorTarget.textContent =
      `You can choose ${this.maxPlaces === 1 ? "one place" : `${this.maxPlaces} places`} with this invitation. ` +
      "Remove a place to choose a different one."
    this.toggle(this.checkoutErrorTarget, true)
  }

  add(event) {
    if (this.atCapacity()) return this.reportCapacity()

    const { placeId, title, location, slug } = event.currentTarget.dataset
    this.selection.set(Number(placeId), { title, location, slug })
    this.track("place_selected", { place_id: Number(placeId), place_title: title, source: "browse" })
    this.render()
    if (this.browsePlaces.length > 0) this.renderBrowse()
  }

  remove(event) {
    const id = Number(event.currentTarget.dataset.placeId)
    const place = this.selection.get(id)
    this.selection.delete(id)
    this.track("place_deselected", { place_id: id, place_title: place?.title })
    this.render()
    if (this.browsePlaces.length > 0) this.renderBrowse()
  }

  async resolvePaste() {
    const response = await fetch(this.resolveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify({ text: this.pasteBoxTarget.value })
    })
    const { resolved, rejected } = await response.json()

    // A paste can carry more places than the invitation has left. Take what
    // fits and say so, rather than silently dropping the rest.
    const room = this.maxPlaces === null ? resolved.length : Math.max(this.maxPlaces - this.selection.size, 0)
    const overflow = resolved.slice(room)

    resolved.slice(0, room).forEach((p) => {
      const isNew = !this.selection.has(p.place_id)
      this.selection.set(p.place_id, { title: p.title, location: p.location, slug: p.slug })
      if (isNew) this.track("place_selected", { place_id: p.place_id, place_title: p.title, source: "paste" })
    })
    this.pasteBoxTarget.value = ""

    const problems = [
      ...rejected.map((r) => `${esc(r.input)} — ${this.reason(r.reason)}`),
      ...overflow.map((p) => `${esc(p.title)} — beyond the ${this.maxPlaces} you can choose`),
    ]
    this.pasteReportTarget.innerHTML = problems.length
      ? `<p class="font-bold">Couldn’t add ${problems.length} of these:</p>
         <ul class="mt-1 list-disc pl-5">${problems.map((problem) => `<li>${problem}</li>`).join("")}</ul>`
      : ""
    this.render()
  }

  reason(code) {
    return {
      not_found: "no matching place",
      not_published: "not published on Atlas Obscura",
      unavailable: "already reserved for a Place Lift"
    }[code] || code
  }

  unitAmount(quantity) {
    const tier = this.tiersValue.find((t) => t.up_to === null || quantity <= t.up_to)
    return tier.unit_amount
  }

  listTotalFor(quantity) {
    return quantity * this.unitAmount(quantity)
  }

  // Mirrors Amplify::Pricing: never quote more than the cheapest higher tier.
  // 10 places list at $50,000 but 11 list at $44,000, so 10 cost $44,000.
  totalFor(quantity) {
    const billable = this.billableQuantity(quantity)
    return billable * this.unitAmount(billable)
  }

  billableQuantity(quantity) {
    if (quantity < 1) return quantity
    const candidates = [quantity, ...this.tierFloors().filter((f) => f > quantity)]
    return candidates.reduce((best, q) =>
      q * this.unitAmount(q) < best * this.unitAmount(best) ? q : best)
  }

  tierFloors() {
    let floor = 1
    return this.tiersValue.map((t) => {
      const current = floor
      if (t.up_to !== null) floor = t.up_to + 1
      return current
    })
  }

  // Under the cap, buying more never costs less -- it costs the same. At 10
  // places you are already paying for 11, so the eleventh is free.
  freePlaces(quantity) {
    return Math.max(this.billableQuantity(quantity) - quantity, 0)
  }



  // Permissive on purpose: the only real test of an address is whether the
  // receipt arrives, so this catches typos and empties without rejecting the
  // unusual-but-valid.
  emailValid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.emailTarget.value.trim())
  }

  contactValid() {
    return this.contactFieldTargets.every((field) => field.value.trim() !== "")
  }

  // Only complain once the buyer has left the field: nagging while they are
  // still typing the first character is noise.
  showEmailError() {
    this.toggle(this.emailErrorTarget, !this.emailValid() && this.emailTarget.value.trim() !== "")
  }

  money(cents) {
    return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
  }

  render() {
    const quantity = this.selection.size
    this.save()

    // Removing is only offered where something could put it back. A proprietor
    // has no browse list and no paste box, so Remove would be a one-way door
    // out of the only thing their invitation buys.
    const removable = this.hasBrowseListTarget || this.hasPasteBoxTarget

    this.selectedTarget.innerHTML = [...this.selection.entries()].map(([id, p]) => `
      <li class="flex items-baseline justify-between gap-4 border-b border-gray-200 py-3 last:border-0">
        <span class="aon-body-small text-aon-black">
          <span class="font-bold">${esc(p.title)}</span>
          <span class="text-gray-400">·</span>
          <span class="aon-body-smaller text-gray-600">${esc(p.location)}</span>
        </span>
        ${removable ? `<button type="button" data-action="amplify-picker#remove" data-place-id="${id}"
                class="aon-body-smaller shrink-0 text-gray-600 underline hover:text-aon-black">
          Remove
        </button>` : ""}
      </li>`).join("")

    this.hiddenFieldsTarget.innerHTML = [...this.selection.keys()]
      .map((id) => `<input type="hidden" name="place_ids[]" value="${id}">`).join("")

    this.toggle(this.emptyTarget, quantity === 0)
    this.countTarget.textContent = quantity === 1 ? "1 place" : `${quantity} places`

    // Keep the button interactive so it can explain what is missing, while
    // aria-disabled and its styling communicate that checkout is unavailable.
    const emailOk = this.emailValid()
    const checkoutReady = quantity > 0 && this.contactValid() && emailOk && this.termsTarget.checked
    this.submitTarget.setAttribute("aria-disabled", String(!checkoutReady))
    this.submitTarget.classList.toggle("cursor-not-allowed", !checkoutReady)
    this.submitTarget.classList.toggle("opacity-40", !checkoutReady)
    this.toggle(this.checkoutErrorTarget, false)
    if (emailOk) this.toggle(this.emailErrorTarget, false)

    // When the volume cap applies, highlight the tier that sets the capped
    // total rather than the higher list rate for the selected quantity.
    const pricingQuantity = this.billableQuantity(quantity)
    this.tierRowTargets.forEach((row) => {
      const upTo = row.dataset.upTo === "" ? null : Number(row.dataset.upTo)
      const active = quantity > 0 && this.unitAmount(pricingQuantity) === Number(row.dataset.unitAmount) &&
        (upTo === null || pricingQuantity <= upTo)
      row.classList.toggle("bg-aon-tan", active)
      row.classList.toggle("font-bold", active)
    })

    if (quantity === 0) {
      this.totalTarget.textContent = "—"
      this.rateTarget.textContent = "Add a place to see your rate"
      this.nudgeTarget.textContent = ""
      this.reportedUnitAmount = null
      this.nudgedToward = null
      return
    }

    const capped = this.totalFor(quantity) < this.listTotalFor(quantity)
    const effectiveRate = Math.round(this.totalFor(quantity) / quantity)

    this.totalTarget.textContent = this.money(this.totalFor(quantity))
    this.rateTarget.textContent = capped
      ? `${this.money(effectiveRate)} per place · Volume pricing applied`
      : `${this.money(this.unitAmount(quantity))} per place`

    const free = this.freePlaces(quantity)
    this.nudgeTarget.textContent = free > 0
      ? `You're already paying for ${quantity + free} places — add ${free} more at no extra cost.`
      : ""

    this.trackPricing(quantity, free)
  }

  // Two pricing moments worth knowing about, each reported once per time it
  // actually happens rather than once per render.
  trackPricing(quantity, free) {
    const unitAmount = this.unitAmount(quantity)
    if (unitAmount !== this.reportedUnitAmount) {
      this.reportedUnitAmount = unitAmount
      this.track("tier_reached", { unit_amount_cents: unitAmount })
    }

    if (free > 0) {
      const target = quantity + free
      if (target !== this.nudgedToward) {
        this.nudgedToward = target
        this.track("volume_nudge_shown", { nudge_target: target, free_places: free })
      }
      return
    }

    // The buyer filled the gap the nudge pointed at: they took the offer.
    if (this.nudgedToward !== null) {
      const target = this.nudgedToward
      this.nudgedToward = null
      if (quantity >= target) this.track("volume_nudge_accepted", { nudge_target: target })
    }
  }

  explainCheckout(event) {
    const missing = []
    if (this.selection.size === 0) missing.push("select at least one place")
    if (!this.contactValid()) missing.push("enter your first and last name")
    if (!this.emailValid()) missing.push("enter a valid email address")
    if (!this.termsTarget.checked) missing.push("view and agree to the Place Lift Terms")
    if (missing.length === 0) return

    event.preventDefault()
    this.checkoutErrorTarget.textContent = `To complete your purchase, ${missing.join(", ")}.`
    this.toggle(this.checkoutErrorTarget, true)
  }

  checkoutStart() {
    this.checkoutStarted = true
    // Every submit creates its own order, and on an offer lead every order
    // spends the invitation. The button only ever carried aria-disabled, which
    // tells assistive tech but does not stop a second click, so take it out of
    // use for the rest of this page's life. The form data set is built after
    // this event, but nothing reads the submit button's own value.
    this.submitTarget.disabled = true
    this.track("checkout_started", {}, { transport: "sendBeacon" })
  }

  releaseCheckout() {
    this.checkoutStarted = false
    this.submitTarget.disabled = false
    this.render()
  }

  // Leaving screen 3 holding a selection nobody paid for. sendBeacon because
  // the page is already on its way out.
  trackAbandon() {
    if (this.checkoutStarted || this.selection.size === 0) return
    this.track("cart_abandoned", {}, { transport: "sendBeacon" })
  }

  // Every event carries the same picture of the cart, so any of them can be
  // read on its own without joining back to a pageview.
  track(event, properties = {}, options = {}) {
    if (typeof window.posthog === "undefined" || !window.posthog.capture) return

    const quantity = this.selection.size
    window.posthog.capture(event, {
      ...this.analyticsValue,
      ...properties,
      place_count: quantity,
      total_cents: quantity > 0 ? this.totalFor(quantity) : 0,
    }, options)
  }
}

// Place titles are user-submitted, so nothing from the server goes into
// innerHTML or an attribute raw.
function esc(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ))
}
